import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase.ts';
import { formatCurrency, formatDate, formatMonthYear, formatPhoneNumber, getOrdinalSuffix } from '../utils/formatters.ts';
import LoadingSpinner from '../components/shared/LoadingSpinner.tsx';
import ErrorMessage from '../components/shared/ErrorMessage.tsx';
import ConfirmationModal from '../components/shared/ConfirmationModal.tsx';
import { Payment } from '../types/payment.ts';
import { useBills } from '../hooks/useBills.ts';
import '../styles/BillDetails.css';

const BillDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billDetails, setBillDetails] = useState<any>(null);
  const [tenantBills, setTenantBills] = useState<any[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { deleteBill } = useBills();

  const fetchBillDetails = useCallback(async () => {
    try {
      const { data: bill, error: billError } = await supabase
        .from('bills')
        .select(`
          *,
          tenant:tenants (
            id,
            full_name,
            phone,
            email,
            monthly_rent,
            security_deposit,
            start_date,
            end_date,
            payment_due_date
          ),
          property:properties (
            id,
            name,
            address,
            property_type,
            planned_rent
          )
        `)
        .eq('id', id)
        .single();

      if (billError) throw billError;

      // Fetch payments for this bill
      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('*')
        .eq('bill_id', id)
        .order('payment_date', { ascending: false });

      if (paymentError) throw paymentError;
      setPayments(paymentData || []);
      setBillDetails(bill);

      // Fetch all bills for this tenant
      if (bill?.tenant_id) {
        const { data: otherBills } = await supabase
          .from('bills')
          .select('*')
          .eq('tenant_id', bill.tenant_id)
          .order('due_date', { ascending: false });
        
        setTenantBills(otherBills || []);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    fetchBillDetails();
  }, [fetchBillDetails]);

  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'status-paid';
      case 'unpaid':
        return 'status-unpaid';
      case 'partial':
        return 'status-partial';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  const handleDelete = () => {
    if (billDetails?.status === 'Cancelled') {
      setShowConfirmDelete(true);
    }
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteBill(billDetails.id);
      navigate('/bills');
    } catch (error) {
      console.error('Error deleting bill:', error);
      alert('Failed to delete bill');
    } finally {
      setIsDeleting(false);
      setShowConfirmDelete(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!billDetails) return <ErrorMessage message="Bill not found" />;

  return (
    <>
    <div className='details-bill'>
    <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/bills')}>
          <span className="back-icon">←</span>
          <span>Back to Bills</span>
        </button>
        <div className="header-content">
          <h1>Bill Details</h1>
          {billDetails?.status === 'Cancelled' && (
            <button 
              onClick={handleDelete} 
              className="btn-delete"
              title="Delete cancelled bill"
            >
              Delete Bill
            </button>
          )}
        </div>
      </div>
    <div className="bill-details-page">
      {/* Left side - History sections */}
      <div className="history-section">
        {/* Bill History */}
        <div className="history-container">
          <h2>Tenant's Bill History</h2>
          <div className="bills-history-table">
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Amount</th>
                  <th>Due Date</th>
                  <th>Status</th>
                  <th>Paid Amount</th>
                </tr>
              </thead>
              <tbody>
                {tenantBills.map(bill => (
                  <tr 
                    key={bill.id} 
                    className={bill.id === id ? 'current-bill' : ''}
                    onClick={() => navigate(`/bills/${bill.id}`)}
                  >
                    <td>{formatMonthYear(bill.month)}</td>
                    <td>{formatCurrency(bill.amount)}</td>
                    <td>{formatDate(bill.due_date)}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(bill.status)}`}>
                        <span className="status-dot"></span>
                        {bill.status}
                      </span>
                    </td>
                    <td>{formatCurrency(bill.paid_amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment History */}
        <div className="history-container">
          <h2>Payment History</h2>
          <div className="payments-table">
            <table>
              <thead>
                <tr>
                  <th>Payment Date</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Paid To</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {payments.length > 0 ? (
                  payments.map((payment) => (
                    <tr 
                      key={payment.id}
                      onClick={() => navigate(`/payments/${payment.id}`)}
                      className="payment-row"
                    >
                      <td>{formatDate(payment.payment_date)}</td>
                      <td>{formatCurrency(payment.total_amount)}</td>
                      <td>{payment.payment_mode}</td>
                      <td>{payment.payment_to}</td>
                      <td>{payment.notes || '-'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="no-data">
                      No payments recorded for this bill
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Right side - Info cards */}
      <div className="info-section">
        {/* Bill Information */}
        <div className="detail-card">
          <h2>Bill Information</h2>
          <div className="detail-content">
            <p><strong>Month:</strong> {formatMonthYear(billDetails.month)}</p>
            <p><strong>Amount:</strong> {formatCurrency(billDetails.amount)}</p>
            <p>
              <strong>Status:</strong> 
              <span className={`status-badge ${getStatusClass(billDetails.status)}`}>
                <span className="status-dot"></span>
                {billDetails.status}
              </span>
            </p>
            <p><strong>Due Date:</strong> {formatDate(billDetails.due_date)}</p>
            {billDetails.payment_date && (
              <p><strong>Payment Date:</strong> {formatDate(billDetails.payment_date)}</p>
            )}
            {billDetails.paid_amount > 0 && (
              <p><strong>Paid Amount:</strong> {formatCurrency(billDetails.paid_amount)}</p>
            )}
          </div>
        </div>

        {/* Tenant Information */}
        <div className="detail-card">
          <h2>Tenant Information</h2>
          <div className="detail-content">
            <p><strong>Name:</strong> {billDetails.tenant.full_name}</p>
            <p><strong>Phone:</strong> {formatPhoneNumber(billDetails.tenant.phone)}</p>
            <p><strong>Email:</strong> {billDetails.tenant.email || <span className="not-provided">Not provided</span>}</p>
            <p><strong>Monthly Rent:</strong> {formatCurrency(billDetails.tenant.monthly_rent)}</p>
            <p><strong>Security Deposit:</strong> {formatCurrency(billDetails.tenant.security_deposit)}</p>
            <p><strong>Payment Due Date:</strong> {getOrdinalSuffix(billDetails.tenant.payment_due_date)} of every month</p>
          </div>
        </div>

        {/* Property Information */}
        <div className="detail-card">
          <h2>Property Information</h2>
          <div className="detail-content">
            <p><strong>Name:</strong> {billDetails.property.name}</p>
            <p><strong>Address:</strong> {billDetails.property.address}</p>
            <p><strong>Type:</strong> {billDetails.property.property_type}</p>
            <p><strong>Planned Rent:</strong> {formatCurrency(billDetails.property.planned_rent)}</p>
          </div>
        </div>
      </div>
    </div>
    </div>

    <ConfirmationModal
      isOpen={showConfirmDelete}
      message="Are you sure you want to delete this cancelled bill? This action cannot be undone."
      onConfirm={confirmDelete}
      onCancel={() => setShowConfirmDelete(false)}
      confirmText="Delete"
      isLoading={isDeleting}
    />
    </>
  );
};

export default BillDetailsPage;
